import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import {
  Icon,
  type IconName,
  XTNDLogo,
  XTNDLogoDark,
  XTNDLogoSquare,
  XTNDLogoSquareDark,
  Button,
} from '@repo/ui';
import { authApi } from '@xtnd/web/features/auth/api';
import { showToast } from '@repo/ui';
import { useDarkMode } from '@xtnd/web/shared/hooks/useDarkMode';
import { useAbility } from '@xtnd/web/providers';
import { PERMISSION_SUBJECT_FEATURES } from '@xtnd/web/features/permissions';
import { AR_COLLECTIONS_ROUTES } from '@xtnd/web/shared/constants';

import { Action } from '../types';

interface NavItem {
  path: string;
  label: string;
  icon: IconName;
  can?: boolean;
  badge?: string | number;
}

interface NavSection {
  title?: string;
  can?: boolean;
  items: NavItem[];
}

interface SidebarProps {
  collapsed: boolean;
}

export function Sidebar({ collapsed }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const ability = useAbility();
  const currentUser = authApi.getCurrentUser();
  const [profileOpen, setProfileOpen] = React.useState(false);
  const { isDark } = useDarkMode();

  // Get environment and version from Vite env variables
  const appEnv = import.meta.env.VITE_APP_ENV || 'dev';
  const appVersion = import.meta.env.VITE_APP_VERSION || '1.0.0';

  // Environment badge configuration
  const envConfig = {
    debug: { label: 'DEBUG', color: 'bg-purple-500 text-white' },
    dev: { label: 'DEV', color: 'bg-blue-500 text-white' },
    uat: { label: 'UAT', color: 'bg-orange-500 text-white' },
    prod: { label: 'PROD', color: 'bg-green-500 text-white' },
  };

  const currentEnv = envConfig[appEnv as keyof typeof envConfig] || envConfig.dev;

  const navSections: NavSection[] = [
    {
      title: 'Main',
      can: ability?.can(Action.READ, PERMISSION_SUBJECT_FEATURES.MAIN),
      items: [
        {
          path: '/juniper-transactions',
          label: 'Transactions',
          icon: 'HiCollection',
          can: ability?.can(Action.READ, PERMISSION_SUBJECT_FEATURES.JUNIPER_TRANSACTIONS),
        },
        {
          path: '/trust-requests/create',
          label: 'Trust Request Creation',
          icon: 'HiDocumentAdd',
          can: ability?.can(Action.READ, PERMISSION_SUBJECT_FEATURES.TRUST_REQUESTS),
        },
      ],
    },
    {
      title: 'Collections',
      can: ability?.can(Action.READ, PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS),
      items: [
        {
          path: '/ar-collections/queue',
          label: 'Collections Queue',
          icon: 'HiCollection',
          can: ability?.can(Action.READ, PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_QUEUE),
        },
        {
          path: AR_COLLECTIONS_ROUTES.AR.EMAIL_QUEUE,
          label: 'Email Queue',
          icon: 'HiEnvelope',
          can: ability?.can(
            Action.READ,
            PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.EMAIL_QUEUE
          ),
        },
      ],
    },
    {
      title: 'Appeals',
      can: ability?.can(
        Action.READ,
        PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.APPEALS
      ),
      items: [
        {
          path: AR_COLLECTIONS_ROUTES.AR.APPEALS,
          label: 'Current Appeals Queue',
          icon: 'HiClipboardList',
          can: ability?.can(
            Action.READ,
            PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.APPEALS
          ),
        },
      ],
    },
    {
      title: 'Write-Offs',
      can: ability?.can(
        Action.READ,
        PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.WRITE_OFF
      ),
      items: [
        {
          path: AR_COLLECTIONS_ROUTES.AR.WRITE_OFFS,
          label: 'Current Write-off Queue',
          icon: 'HiOutlinePencilAlt',
          can: ability?.can(
            Action.READ,
            PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.WRITE_OFF
          ),
        },
      ],
    },
    {
      title: 'Reports',
      can: ability?.can(
        Action.READ,
        PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.BASE
      ),
      items: [
        {
          path: AR_COLLECTIONS_ROUTES.AR.PERFORMANCE,
          label: 'Performance',
          icon: 'HiTrendingUp',
          can: ability?.can(
            Action.READ,
            PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.PERFORMANCE
          ),
        },
        {
          path: AR_COLLECTIONS_ROUTES.AR.LEDGER_REPORTS,
          label: 'Ledger Reports',
          icon: 'HiChartBar',
          can: ability?.can(
            Action.READ,
            PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.LEDGER_REPORTS
          ),
        },
        {
          path: AR_COLLECTIONS_ROUTES.AR.ESCALATION_REPORT,
          label: 'Escalation Report',
          icon: 'HiFlag',
          can: ability?.can(
            Action.READ,
            PERMISSION_SUBJECT_FEATURES.AR_COLLECTIONS_REPORTING.ESCALATION_REPORT
          ),
        },
      ],
    },
    {
      title: 'Analytics',
      can: ability?.can(Action.READ, PERMISSION_SUBJECT_FEATURES.ANALYTICS),
      items: [
        {
          path: '/reports',
          label: 'Reports',
          icon: 'HiChartBar',
          can: ability?.can(Action.READ, PERMISSION_SUBJECT_FEATURES.REPORTS),
        },
      ],
    },
    {
      title: 'Support',
      can: ability?.can(Action.READ, PERMISSION_SUBJECT_FEATURES.SUPPORT),
      items: [
        {
          path: '/support/feedback',
          label: 'Feedback',
          icon: 'HiChat',
          can: ability?.can(Action.READ, PERMISSION_SUBJECT_FEATURES.FEEDBACK),
        },
      ],
    },
  ];

  const handleLogout = async () => {
    try {
      await authApi.signOut();
      showToast.success('Logged out successfully');
      navigate('/login?logout=true', { replace: true, state: null });
    } catch {
      showToast.error('Failed to log out');
    }
  };

  const renderNavSection = (section: NavSection, sectionIdx: number) => {
    if (!section.can) {
      return;
    }

    return (
      <div key={sectionIdx}>
        {/* Section Title */}
        {!collapsed && section.title && (
          <div className="px-3 mb-3">
            <h3 className="text-xs font-bold text-xtnd-dark-400 dark:text-xtnd-light uppercase tracking-wider">
              {section.title}
            </h3>
          </div>
        )}

        {/* Section Items */}
        <div className="space-y-1">
          {section.items.map(item => {
            // Check if current path starts with item path (for nested routes)
            if (!item.can) {
              return null;
            }

            // Check if current path matches item path
            const isActive =
              location.pathname === item.path || location.pathname.startsWith(item.path + '/');

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`
              group relative flex items-center gap-3 px-4 py-3 rounded-lg
              text-sm font-medium transition-all duration-200
              ${
                isActive
                  ? 'bg-xtnd-blue text-xtnd-white shadow-lg shadow-xtnd-blue/30'
                  : 'text-xtnd-dark-600 dark:text-xtnd-light hover:bg-xtnd-light dark:hover:bg-xtnd-dark-700 hover:text-xtnd-dark dark:hover:text-xtnd-white'
              }
              ${collapsed ? 'justify-center px-0' : ''}
            `}
                title={collapsed ? item.label : undefined}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon
                  name={item.icon}
                  size="md"
                  className="transition-transform group-hover:scale-110"
                />
                {!collapsed && (
                  <>
                    <span className="flex-1">{item.label}</span>
                    {item.badge && (
                      <span className="px-2.5 py-1 text-xs font-bold rounded-lg bg-xtnd-white/20 backdrop-blur-sm">
                        {item.badge}
                      </span>
                    )}
                    {isActive && (
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-xtnd-white rounded-l-full" />
                    )}
                  </>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <aside
      className={`
        fixed left-0 top-0 z-40 h-screen
        bg-gradient-to-b from-xtnd-white to-xtnd-light/30 dark:from-xtnd-dark dark:to-xtnd-dark-900
        shadow-xl
        transition-all duration-300 ease-in-out
        ${collapsed ? 'w-20' : 'w-72'}
      `}
      role="navigation"
      aria-label="Sidebar"
    >
      <div className="flex flex-col h-full">
        {/* Header with Logo, Environment Badge, and Version */}
        <div className="flex flex-col items-center justify-center px-2 pt-2 pb-2 mb-2">
          {/* Logo with Environment Badge */}
          <div className="relative mb-2 inline-block">
            <Link to="/" className="flex items-center">
              {collapsed ? (
                isDark ? (
                  <XTNDLogoSquareDark size="lg" />
                ) : (
                  <XTNDLogoSquare size="lg" />
                )
              ) : isDark ? (
                <XTNDLogoDark size="lg" />
              ) : (
                <XTNDLogo size="lg" />
              )}
            </Link>

            {/* Environment Badge - Diagonal Top Right Corner (Flutter style) */}
            {!collapsed && (
              <div className="absolute -top-2 -right-2 overflow-hidden w-16 h-16 pointer-events-none">
                <div
                  className={`
                    absolute top-3 -right-8 w-24 text-center
                    px-1 py-0.5 text-[8px] font-extrabold uppercase tracking-wide
                    shadow-lg transform rotate-45 origin-center
                    ${currentEnv.color}
                  `}
                  title={`Environment: ${currentEnv.label}`}
                >
                  {currentEnv.label}
                </div>
              </div>
            )}
          </div>

          {/* Version Number - Below Logo */}
          {!collapsed && (
            <div className="text-[10px] font-medium text-xtnd-dark-400 dark:text-xtnd-light tracking-wider">
              v{appVersion}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="mx-4 mb-4 h-px bg-gradient-to-r from-transparent via-xtnd-light dark:via-xtnd-dark-700 to-transparent" />

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-4 space-y-6">
          {navSections.map((section, sectionIdx) => renderNavSection(section, sectionIdx))}
        </nav>

        {/* User Profile */}
        <div className="p-4">
          {currentUser && (
            <div className="relative">
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                className={`
                  w-full p-3 rounded-xl
                  bg-xtnd-light/50 dark:bg-xtnd-dark-700/50
                  hover:bg-xtnd-light dark:hover:bg-xtnd-dark-600
                  transition-all duration-200
                  ${collapsed ? 'flex justify-center' : ''}
                `}
              >
                <div className={`flex items-center gap-3 ${collapsed ? 'flex-col' : ''}`}>
                  <div className="relative">
                    {currentUser?.picture ? (
                      <img
                        src={currentUser.picture}
                        alt={currentUser.name}
                        className="h-10 w-10 rounded-full ring-2 ring-xtnd-blue/20"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-xtnd-blue to-xtnd-blue-600 text-xtnd-white flex items-center justify-center font-bold shadow-lg">
                        {(currentUser.name || 'U').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 bg-green-500 rounded-full border-2 border-xtnd-white dark:border-xtnd-dark" />
                  </div>
                  {!collapsed && (
                    <div className="flex-1 min-w-0 text-left">
                      <div className="text-sm font-bold text-xtnd-dark dark:text-xtnd-white truncate">
                        {currentUser.name}
                      </div>
                      <div className="text-xs text-xtnd-dark-500 dark:text-xtnd-light truncate">
                        {currentUser.email}
                      </div>
                    </div>
                  )}
                  {!collapsed && (
                    <Icon
                      name={profileOpen ? 'HiChevronUp' : 'HiChevronDown'}
                      size="sm"
                      className="text-xtnd-dark-400 dark:text-xtnd-light"
                    />
                  )}
                </div>
              </button>

              {/* Profile Dropdown */}
              {profileOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setProfileOpen(false)}
                    onKeyDown={e => e.key === 'Escape' && setProfileOpen(false)}
                    role="button"
                    tabIndex={0}
                    aria-label="Close profile menu"
                  />
                  <div className="absolute bottom-0 left-full ml-2 w-72 bg-xtnd-white dark:bg-xtnd-dark rounded-xl shadow-xl border border-xtnd-light dark:border-xtnd-dark-700 z-20 overflow-hidden">
                    <div className="p-4">
                      <div className="mb-3 pb-3 border-b border-xtnd-light dark:border-xtnd-dark-700">
                        <div className="text-xs font-bold text-xtnd-dark dark:text-xtnd-white mb-2">
                          Tenant Information
                        </div>
                        <div className="space-y-1.5 text-xs">
                          <div className="grid grid-cols-[80px_1fr] gap-2 items-start">
                            <span className="text-xtnd-dark-400 dark:text-xtnd-light">
                              Tenant ID:
                            </span>
                            <span className="text-xtnd-dark dark:text-xtnd-white font-medium break-all">
                              {currentUser.tenantId || '—'}
                            </span>
                          </div>
                          <div className="grid grid-cols-[80px_1fr] gap-2 items-start">
                            <span className="text-xtnd-dark-400 dark:text-xtnd-light">Alias:</span>
                            <span className="text-xtnd-dark dark:text-xtnd-white font-medium break-all">
                              {currentUser.tenantAlias || '—'}
                            </span>
                          </div>
                          <div className="grid grid-cols-[80px_1fr] gap-2 items-start">
                            <span className="text-xtnd-dark-400 dark:text-xtnd-light">Name:</span>
                            <span className="text-xtnd-dark dark:text-xtnd-white font-medium break-words">
                              {currentUser.tenantName || '—'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        fullWidth
                        onClick={() => {
                          setProfileOpen(false);
                          handleLogout();
                        }}
                        leftIcon={<Icon name="HiLogout" size="sm" />}
                      >
                        Logout
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
